/**
 * CourseItem — reusable course row used across all dashboard states.
 * Supports default, active (in-progress), and completed visual variants.
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { DashboardCourse, DashboardEnrolledCourse, DashboardCompletedCourse } from '../types';

type CourseVariant = 'default' | 'active' | 'completed';

interface CourseItemProps {
  course: DashboardCourse | DashboardEnrolledCourse | DashboardCompletedCourse;
  variant?: CourseVariant;
  meta?: string;
}

const borderByVariant: Record<CourseVariant, string> = {
  default: '#e8e8ee',
  active: '#5b6cff',
  completed: '#00c2a8',
};

const bgByVariant: Record<CourseVariant, string> = {
  default: '#ffffff',
  active: 'rgba(91,108,255,0.04)',
  completed: 'rgba(0,194,168,0.04)',
};

export const CourseItem: React.FC<CourseItemProps> = ({
  course,
  variant = 'default',
  meta,
}) => {
  const progressPct =
    'progressPct' in course ? (course as DashboardEnrolledCourse).progressPct : 0;
  const completedAt =
    'completedAt' in course ? (course as DashboardCompletedCourse).completedAt : null;
  const xpEarned =
    'xpEarned' in course ? (course as DashboardCompletedCourse).xpEarned : 0;

  return (
    <Link
      to={`/courses/${course.id}`}
      className="flex items-start gap-4 p-4 rounded-2xl border transition-all duration-200 hover:translate-x-0.5 hover:shadow-sm block"
      style={{
        borderColor: borderByVariant[variant],
        borderWidth: variant === 'active' ? '2px' : '1.5px',
        background: bgByVariant[variant],
        transition: 'border-color 0.18s, background 0.18s, transform 0.18s, box-shadow 0.18s',
        textDecoration: 'none',
      }}
    >
      <div className="text-2xl flex-shrink-0 mt-0.5">{course.emoji}</div>

      <div className="flex-1 min-w-0">
        {/* Title row */}
        <div className="flex items-center gap-2 flex-wrap mb-0.5">
          {variant === 'completed' && (
            <span className="text-sm" style={{ color: '#00c2a8' }}>✓</span>
          )}
          <p className="font-montserrat font-bold text-sm text-neutral-800 leading-snug">
            {course.title}
          </p>
          {variant === 'completed' && (
            <span className="text-sm">🏅</span>
          )}
        </div>

        {/* Meta */}
        <p className="font-open-sans text-xs text-neutral-500 leading-snug">
          {meta ??
            [
              `${course.durationMin} min`,
              course.category,
              completedAt && `Completed ${completedAt}`,
              xpEarned > 0 && variant === 'completed' && `+${xpEarned} XP`,
            ]
              .filter(Boolean)
              .join(' · ')}
        </p>

        {/* Progress bar — active variant only */}
        {variant === 'active' && progressPct > 0 && (
          <div className="mt-2">
            <div className="flex justify-between text-xs text-neutral-500 mb-1">
              <span>{progressPct}% complete</span>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: '#e8e8ee' }}>
              <div
                className="h-full rounded-full"
                style={{
                  width: `${progressPct}%`,
                  background: 'linear-gradient(90deg, #5b6cff, #7c8eff)',
                  transition: 'width 0.8s ease',
                }}
              />
            </div>
          </div>
        )}
      </div>
    </Link>
  );
};
